import { useState } from 'react'
import { X, Activity, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { rejectRegistration } from '@/supabase-client'

const PORTAL_ADMIN_URL = 'https://www.glasgowmushroomcompany.co.uk/portal/admin/registrations'

const STAGE_META = {
  approved: {
    label: 'Invited',
    description: 'Magic link sent — awaiting first click',
    pillClass: 'bg-amber-100 text-amber-800 border-amber-200',
    dotClass: 'bg-amber-500',
  },
  onboarding_in_progress: {
    label: 'Filling form',
    description: 'Clicked link; partway through the onboarding form',
    pillClass: 'bg-blue-100 text-blue-800 border-blue-200',
    dotClass: 'bg-blue-500',
  },
  submitted_for_review: {
    label: 'Awaiting review',
    description: 'Form submitted — needs step-4 review in portal admin',
    pillClass: 'bg-purple-100 text-purple-800 border-purple-200',
    dotClass: 'bg-purple-500',
  },
}

function relativeTime(iso) {
  if (!iso) return '—'
  const ms = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(ms / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 14) return `${days}d ago`
  const weeks = Math.floor(days / 7)
  return `${weeks}w ago`
}

function RejectForm({ onCancel, onConfirm, busy }) {
  const [reason, setReason] = useState('')
  return (
    <div className="mt-2 p-3 rounded-md bg-muted/30 border border-border">
      <label className="block text-xs text-muted-foreground mb-1">
        Reason for rejection (optional — stored internally)
      </label>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={2}
        className="w-full text-sm rounded border border-input px-2 py-1 bg-background focus:outline-none focus:ring-1 focus:ring-ring"
      />
      <div className="mt-2 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="destructive"
          size="sm"
          disabled={busy}
          onClick={() => onConfirm(reason.trim() || null)}
        >
          {busy ? 'Rejecting…' : 'Confirm reject'}
        </Button>
      </div>
    </div>
  )
}

export default function InProgressRegistrationsPanel({ rows }) {
  const [busyId, setBusyId] = useState(null)
  const [rejectingId, setRejectingId] = useState(null)
  const [error, setError] = useState(null)

  const handleReject = async (id, reason) => {
    setError(null)
    setBusyId(id)
    try {
      await rejectRegistration(id, reason)
      setRejectingId(null)
    } catch (e) {
      setError(`Reject failed: ${e.message}`)
    } finally {
      setBusyId(null)
    }
  }

  if (rows.length === 0 && !error) return null

  return (
    <section className="mb-6">
      <div className="flex items-center gap-2 mb-2 px-1">
        <Activity className="size-3.5 text-muted-foreground" />
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          In progress
        </h3>
        <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-semibold rounded-full bg-muted text-foreground">
          {rows.length}
        </span>
      </div>

      {error && (
        <div className="mb-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
          {error}
        </div>
      )}

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
              <th className="text-left font-medium px-4 py-2">Business</th>
              <th className="text-left font-medium px-4 py-2">Contact</th>
              <th className="text-left font-medium px-4 py-2">Stage</th>
              <th className="text-left font-medium px-4 py-2">Since invited</th>
              <th className="text-right font-medium px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const meta = STAGE_META[row.status] ?? STAGE_META.approved
              const isBusy = busyId === row.id
              const isRejecting = rejectingId === row.id
              return (
                <>
                  <tr key={row.id} className="border-b border-border last:border-b-0">
                    <td className="px-4 py-2.5 font-medium">{row.business_name}</td>
                    <td className="px-4 py-2.5">
                      <div className="text-foreground">{row.contact_name}</div>
                      <div className="text-xs text-muted-foreground">{row.email}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-medium ${meta.pillClass}`}
                        title={meta.description}
                      >
                        <span className={`size-1.5 rounded-full ${meta.dotClass}`} />
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">
                      {relativeTime(row.approved_at)}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {!isRejecting && (
                        <div className="inline-flex gap-2">
                          {row.status === 'submitted_for_review' && (
                            <a
                              href={PORTAL_ADMIN_URL}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline"
                              title="Open in portal admin to review the submitted application"
                            >
                              Review <ExternalLink className="size-3" />
                            </a>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={isBusy}
                            onClick={() => setRejectingId(row.id)}
                            className="h-7 px-2 text-red-700 hover:bg-red-50 hover:text-red-800"
                          >
                            <X className="size-3.5 mr-1" />
                            Reject
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                  {isRejecting && (
                    <tr>
                      <td colSpan={5} className="px-4 pb-3">
                        <RejectForm
                          busy={isBusy}
                          onCancel={() => setRejectingId(null)}
                          onConfirm={(reason) => handleReject(row.id, reason)}
                        />
                      </td>
                    </tr>
                  )}
                </>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}
